import React from 'react';
import { IconClose } from '@noths/polaris-client-ribbons-design-system';
import Link from 'next/link';

import type {
  FacetOptionWithResetHref,
  FacetWithResetHrefs,
} from 'src/adapters/storeToPageQueryString/attachHrefToUIFacets';
import { setPendingProductsQueryURLSyncIntent } from 'src/hooks/useSyncProductsQueryFromUrl/productsQueryURLSyncIntent';
import { TriggerName } from 'src/redux/products/types';
import * as styles from './ActiveFacets.styles';

interface ActiveFacetsProps {
  activeFacets: FacetWithResetHrefs[];
  clearAllHref: string;
}

export const ActiveFacets = ({ activeFacets, clearAllHref }: ActiveFacetsProps) => {
  const activeFacetOptions = activeFacets.reduce((acc, facet) => {
    facet.options.forEach((option) => {
      const href = option.href;

      if (option.active && href) {
        acc.push({ facet, option: { ...option, href } });
      }
    });

    return acc;
  }, [] as { facet: FacetWithResetHrefs; option: FacetOptionWithResetHref & { href: string } }[]);

  if (activeFacets.length === 0) {
    return null;
  }

  return (
    <ul css={styles.list}>
      {activeFacetOptions.map(({ facet, option }) => (
        <li key={`${facet.key}-${option.value}`}>
          <Link
            aria-label={`Clear the "${option.title}" filter`}
            css={styles.resetFacetButton}
            href={option.href}
            onClick={() => {
              setPendingProductsQueryURLSyncIntent({
                href: option.href,
                metadata: {
                  facetTitle: facet.title,
                  optionActive: false,
                  optionTitle: option.title,
                },
                triggerName: TriggerName.RESET_FACET,
              });
            }}
            prefetch={false}
            rel="nofollow"
            scroll={false}
            shallow
          >
            {option.title} <IconClose color="var(--brand-purple-base)" />
          </Link>
        </li>
      ))}
      <li>
        <Link
          aria-label="Clear all filters"
          css={styles.resetAllButton}
          href={clearAllHref}
          onClick={() => {
            setPendingProductsQueryURLSyncIntent({
              href: clearAllHref,
              triggerName: TriggerName.RESET_ALL_FACETS_AND_SORT,
            });
          }}
          prefetch={false}
          rel="nofollow"
          scroll={false}
          shallow
        >
          Clear all
        </Link>
      </li>
    </ul>
  );
};
